import { find, defaultTo, pipe } from 'ramda';
import {
  debugColumnDefsVy,
  defaultColumnDefsVy,
  defaultIdColumnsVy,
  inlineColumnDefsVy,
} from './columnDefsVy';

import {
  IObjektVy,
  IBusinessCollectionProps,
} from '../../../../../Types/ObjektVy';
import { EKVyer } from '../../../Ekonomi/ObjektVyer/EK';
import { LRVyer } from '../../../Leverantor/ObjektVyer/LR';
import { SYSVyer } from '../../../SystemAdministration/ObjektVyer/SYS';
import { OPVyer } from '../../../OP/ObjektVyer/OPObjektVyer';
import { findCollectionStatic } from '../../../../../Model/avenyStaticModelUtils';
import { internalErrorBusinessComp } from '../../../../../Components/InternalError/InternalError';
import { initConst } from '../../../../../Utils/utils';
import { standardVy } from '../../../../../Utils/ObjektVyUtils';
import { BOVyer } from '../../../Bokning/ObjektVyer/BO';
import { ARVyer } from '../../../Anläggning/ObjektVyer/AR';
import { DIVyer } from '../../../Diarie/ObjektVyer/DI';
import { GAVyer } from '../../../Grav/ObjektVyer/GA';
import { KJVyer } from '../../../Kremationsjournal/ObjektVyer/KJ';
import { KRVyer } from '../../../Kundreskontra/ObjektVyer/KR';
import { LAVyer } from '../../../Lager/ObjektVyer/LA';
import { MUVyer } from '../../../Musik/ObjektVyer/MU';
import { OFVyer } from '../../../OrderFakt/ObjektVyer/OF';
import { PUVyer } from '../../../PU/ObjektVyer/PU';
import { TIVyer } from '../../../Tidredovisning/ObjektVyer/TI';
import { BJVyer } from '../../../BisattningJournal/ObjektVyer/BJ';
import { findRutinOrStyrvärde } from '../../Utils/RutinUtils';

export interface IComponentsConfig {
  [index: string]: IObjektVy[];
}

const businessCollectionComponentsConfig = initConst(() => ({
  ...ARVyer(),
  ...BOVyer(),
  ...BJVyer(),
  ...DIVyer(),
  ...EKVyer(),
  ...GAVyer(),
  ...KJVyer(),
  ...KRVyer(),
  ...LAVyer(),
  ...LRVyer(),
  ...MUVyer(),
  ...OFVyer(),
  ...OPVyer(),
  ...PUVyer(),
  ...SYSVyer(),
  ...TIVyer(),
}));

// byt typ på en vy
export const bytTyp =
  (typ: string) =>
  (vy: IObjektVy): IObjektVy => ({
    type: vy.type,
    id: typ,
    info: vy.info,
    def: vy.def,
  });

// defaultvy för "standard". Visar samma fält som är standard för kolumnerna.
// Är tänkt för att man alltid ska få upp något för alla delar under tiden man utvecklar
export const defaultStandardDef: IObjektVy = standardVy([defaultColumnDefsVy]);

// debugvy
export const debugStandardDef: IObjektVy = standardVy([debugColumnDefsVy]);

export const inlineStandardDef: IObjektVy =
  bytTyp('inlineStandard')(inlineColumnDefsVy);

// visar första kolumnen + ev. BENAMNING
export const defaultMiniVy: IObjektVy = bytTyp('mini')(defaultIdColumnsVy);

// defaulvyer för objekt som vi inte definierat vyer för
const defaultConfig: IObjektVy[] = [defaultColumnDefsVy, defaultMiniVy];

// alla vyer för en viss collection
const businessCollectionComponents: (value: string) => IObjektVy[] = (
  collectionName: string
) => {
  const collections = businessCollectionComponentsConfig();
  return collections[collectionName] || defaultConfig;
};

// standardkomponent för en collection. Använder default enligt ovan om ingen är definierad
export const standardkomponent = (
  name: string,
  inline?: boolean,
  rutinKod?: string
): React.ComponentType<React.PropsWithChildren<IBusinessCollectionProps>> => {
  let viewId: string = 'standard';
  const bmod = findCollectionStatic(name);
  if (rutinKod) {
    const rutin = findRutinOrStyrvärde(rutinKod);
    if (rutin?.detaljVy != null) {
      viewId = rutin.detaljVy;
    }
  }

  if (bmod) {
    return pipe(
      businessCollectionComponents,
      find(vy => vy.id === viewId),
      inline ? defaultTo(inlineStandardDef) : defaultTo(defaultStandardDef),
      config => config.def(bmod).comp
    )(name);
  }
  return internalErrorBusinessComp(
    'dd77ee70-cb5c-4bf7-928e-f286bad9512b',
    `collection saknas för ${name}`
  );
};

// minikomponent för en collection. Använder default enligt ovan om ingen är definierad
export const minikomponent = (name: string) => {
  const bmod = findCollectionStatic(name);
  if (bmod) {
    return pipe(
      businessCollectionComponents,
      find(vy => vy.id === 'mini'),
      defaultTo(defaultStandardDef),
      config => config.def(bmod).comp
    )(name);
  }

  return undefined;
};

export default businessCollectionComponents;
